import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Code, Monitor, CreditCard, ShieldCheck, Headphones, Rocket, Gift, TreePine, Phone, Wrench, TestTube, ArrowRight, PoundSterling, TrendingUp, Award } from "lucide-react";
import { fadeUp, stagger, viewportOnce, viewportOnceSmall } from "../utils/motionVariants";
import ResponsiveAccordion from "./ResponsiveAccordion";

const PLATFORM_LOGOS = [
  { name: "WooCommerce", to: "/partners/integrations/woocommerce" },
  { name: "Shopify", to: "/partners/integrations/shopify" },
  { name: "Magento 2", to: "/partners/integrations/magento" },
  { name: "PrestaShop", to: "/partners/integrations/prestashop" },
  { name: "BigCommerce", to: "/partners/integrations/bigcommerce" },
  { name: "OpenCart", to: "/partners/integrations/opencart" },
  { name: "Drupal", to: "/partners/integrations/drupal" },
];

export function EyebrowStrip() {
  const items = [
    { icon: PoundSterling, text: "Earn monthly recurring commission" },
    { icon: Headphones, text: "UK-based human support" },
    { icon: Gift, text: "Win an iPad — every new partner, every month" },
    { icon: TreePine, text: "We plant a tree for every referral" },
  ];
  return (
    <section className="py-6 bg-brand-aqua-light border-y border-brand-aqua/10" data-testid="eyebrow-strip">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((item) => (
            <div key={item.text} className="flex items-center gap-2">
              <item.icon size={16} className="text-brand-aqua flex-shrink-0" />
              <span className="text-sm font-semibold text-brand-black">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function IntegrationsRow() {
  return (
    <section className="py-20 bg-white" data-testid="integrations-row">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Supported Platforms</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight">Choose your platform</h2>
          <p className="text-sm text-brand-grey max-w-2xl mx-auto mt-3">
            Plug-and-play integrations for the platforms your clients already use. Click a platform below to see the full integration details.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {PLATFORM_LOGOS.map((p) => (
            <Link key={p.name} to={p.to} className="flex items-center justify-between gap-2 px-5 py-4 bg-white border border-brand-light-grey rounded-xl text-sm font-bold text-brand-black hover:bg-brand-aqua-light hover:border-brand-aqua/40 hover:text-brand-aqua-dark transition-all group service-card" data-testid={`platform-link-${p.name.toLowerCase().replace(/\s+/g, '-')}`}>
              {p.name}
              <ArrowRight size={14} className="text-brand-grey group-hover:text-brand-aqua transition-colors" />
            </Link>
          ))}
          <Link to="/partners/api" className="flex items-center justify-between gap-2 px-5 py-4 bg-brand-aqua-light border border-brand-aqua/20 rounded-xl text-sm font-bold text-brand-aqua-dark hover:bg-brand-aqua hover:text-white transition-all group" data-testid="platform-link-custom-api">
            Custom API
            <Code size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

export function DeveloperTools() {
  return (
    <section className="py-16 bg-gray-50" data-testid="developer-tools">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border border-brand-light-grey rounded-2xl p-8 flex flex-col md:flex-row gap-6 items-start">
          <div className="w-12 h-12 rounded-xl bg-brand-aqua-light flex items-center justify-center flex-shrink-0">
            <Code size={22} className="text-brand-aqua" />
          </div>
          <div>
            <h3 className="font-heading text-xl font-bold text-brand-black mb-2">Custom API Integration</h3>
            <p className="text-brand-grey leading-relaxed">
              Full flexibility for bespoke builds, SaaS platforms and advanced checkout experiences. Whether you're building for SMBs or scaling enterprise ecommerce, CanDo Payments fits your stack. Sandbox access, clear documentation, test cards and a dedicated solution engineer come as standard.
            </p>
            <Link to="/partners/api" className="inline-flex items-center gap-1.5 mt-4 text-brand-aqua font-semibold text-sm hover:gap-2 transition-all" data-testid="dev-tools-api-link">
              Explore the API <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export function DemoAccess() {
  const items = [
    { icon: Monitor, title: "Live demo store", desc: "Walk through a real checkout journey exactly as your customers will see it. No sign-up, no friction." },
    { icon: Wrench, title: "Back office access", desc: "Explore the reporting, settlement and transaction management tools you'll use every day." },
    { icon: TestTube, title: "Test cards", desc: "Simulate real-world payment scenarios — approvals, declines, 3-D Secure challenges, refunds." },
  ];
  return (
    <section className="py-20 bg-white" data-testid="demo-access">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-12">
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Demo Access</motion.p>
          <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">Get hands-on before you integrate</motion.h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={viewportOnceSmall} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((item) => (
            <motion.div key={item.title} variants={fadeUp} className="bg-gray-50 border border-brand-light-grey rounded-2xl p-6">
              <item.icon size={24} className="text-brand-aqua mb-4" />
              <h3 className="font-heading font-bold text-brand-black mb-2">{item.title}</h3>
              <p className="text-sm text-brand-grey leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>
        <p className="text-center text-sm text-brand-grey mt-6">Everything you need to build with confidence. See exactly what your clients will experience before you deploy.</p>
      </div>
    </section>
  );
}

export function Benefits() {
  const items = [
    { icon: CreditCard, title: "Payments", desc: "Lower transaction fees than Stripe, PayPal, Worldpay and Dojo. Secure, PCI-compliant processing. Subscription and recurring billing built in." },
    { icon: Rocket, title: "Performance", desc: "Fast, optimised checkout experiences. Reliable infrastructure powered by Elavon (U.S. Bank). Built to scale with your ecommerce business." },
    { icon: Headphones, title: "Support", desc: "Dedicated onboarding. Real human support — no ticket black holes. Developer-friendly documentation and a team you can actually speak to." },
  ];
  return (
    <section className="py-20 bg-gray-50" data-testid="benefits-block">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-12">
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Benefits</motion.p>
          <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">Everything you need. Nothing you don't.</motion.h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={viewportOnceSmall} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((item) => (
            <motion.div key={item.title} variants={fadeUp} className="bg-white border border-brand-light-grey rounded-2xl p-8 shadow-sm">
              <item.icon size={28} className="text-brand-aqua mb-4" />
              <h3 className="font-heading text-lg font-bold text-brand-black mb-2">{item.title}</h3>
              <p className="text-sm text-brand-grey leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export function HowItWorks() {
  const steps = [
    { num: "01", title: "Integrate", desc: "Use our plugins or API to connect CanDo Payments to your platform. Clear docs, sandbox access, and a real engineer on the other end of the line." },
    { num: "02", title: "Launch", desc: "Go live with a fully supported, secure payment solution. We'll walk you through onboarding and make sure your first transaction goes through cleanly." },
    { num: "03", title: "Earn", desc: "Join the Partner Programme and generate recurring commission on every client you bring across. Build once. Earn continuously." },
  ];
  return (
    <section className="py-20 bg-white" data-testid="how-it-works-partner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-12">
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">How It Works</motion.p>
          <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">Simple to build. Powerful to scale. Profitable to keep.</motion.h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((s, i) => (
            <motion.div key={s.num} variants={fadeUp} className="text-center relative">
              <div className="w-16 h-16 rounded-2xl bg-brand-aqua text-white font-heading text-2xl font-bold flex items-center justify-center mx-auto mb-5">{s.num}</div>
              <h3 className="font-heading text-xl font-bold text-brand-black mb-3">{s.title}</h3>
              <p className="text-sm text-brand-grey leading-relaxed max-w-xs mx-auto">{s.desc}</p>
              {i < 2 && <div className="hidden md:block absolute top-8 left-[60%] w-[80%] border-t-2 border-dashed border-brand-light-grey" />}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export function PartnerBenefits() {
  const items = [
    { icon: TrendingUp, title: "Recurring commission", desc: "Every successful referral earns you a share of the revenue — not a one-off kickback. As your client grows, so do you." },
    { icon: Award, title: "Tiered incentives", desc: "The more businesses you bring across, the more you earn. Boost your rate the moment you pass each tier." },
    { icon: Gift, title: "Win an iPad", desc: "Every new partner who signs up this month is entered into our monthly iPad giveaway. Because saying thank you shouldn't just be an email." },
  ];
  return (
    <section className="py-20 bg-gray-50" data-testid="partner-benefits">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-12">
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Partner Benefits</motion.p>
          <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">Build once. Earn continuously.</motion.h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={viewportOnceSmall} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((item) => (
            <motion.div key={item.title} variants={fadeUp} className="bg-white border border-brand-light-grey rounded-2xl p-8 shadow-sm">
              <item.icon size={28} className="text-brand-aqua mb-4" />
              <h3 className="font-heading text-lg font-bold text-brand-black mb-2">{item.title}</h3>
              <p className="text-sm text-brand-grey leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export function TrustedProven() {
  return (
    <section className="py-20 bg-white" data-testid="trusted-proven">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger}>
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3 text-center">Trusted & Proven</motion.p>
          <motion.p variants={fadeUp} className="text-center text-brand-grey leading-relaxed max-w-3xl mx-auto mb-12">
            CanDo Payments is powered by Elavon, the merchant services arm of U.S. Bank and one of the largest payment processors in the world. Elavon is trusted by global brands including Virgin Atlantic, IKEA, Hilton and British Airways — and their infrastructure powers the card transactions processed by the businesses we support.
          </motion.p>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={viewportOnceSmall} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, title: "Enterprise-grade security", desc: "PCI DSS compliant end-to-end. Tokenisation, 3-D Secure and AI-powered real-time fraud scoring come as standard." },
            { icon: Rocket, title: "Proven reliability", desc: "Cloud-based infrastructure with built-in redundancy. The same platform processing for some of the world's biggest brands." },
            { icon: PoundSterling, title: "Real payment backing", desc: "Powered by U.S. Bank — the fifth-largest bank in the United States. Your clients' money is in serious hands." },
          ].map((item, i) => (
            <motion.div key={item.title} variants={fadeUp} className="bg-gray-50 border border-brand-light-grey rounded-2xl p-6">
              <item.icon size={24} className="text-brand-aqua mb-4" />
              <h3 className="font-heading font-bold text-brand-black mb-2">{item.title}</h3>
              <p className="text-sm text-brand-grey leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>
        <p className="text-center text-sm text-brand-grey mt-8">Plus, for every successful referral, we plant a tree with Ecologi — making a daily difference while you grow your business.</p>
      </div>
    </section>
  );
}

export function PartnerCTA() {
  return (
    <section className="py-20 cta-section-bg" data-testid="partner-cta">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center text-white mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/70 mb-3">Choose Your Next Step</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Ready to build something better?</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
            <Phone size={28} className="text-white mx-auto mb-4" />
            <h3 className="font-heading font-bold text-white mb-2">Book a call</h3>
            <p className="text-sm text-white/80 leading-relaxed mb-4">Speak with our team to discuss your integration or client needs. No pitch, just a real conversation.</p>
            <a href="/book" className="inline-block bg-white text-brand-aqua-dark font-semibold rounded-full px-6 py-2.5 text-sm hover:bg-white/90 transition-colors" data-testid="partner-cta-book-call">Book a Call</a>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
            <Code size={28} className="text-white mx-auto mb-4" />
            <h3 className="font-heading font-bold text-white mb-2">Access sandbox</h3>
            <p className="text-sm text-white/80 leading-relaxed mb-4">Get your sandbox credentials and start building immediately.</p>
            <Link to="/contact" className="inline-block bg-white text-brand-aqua-dark font-semibold rounded-full px-6 py-2.5 text-sm hover:bg-white/90 transition-colors" data-testid="partner-cta-sandbox">Get Sandbox Access</Link>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
            <CheckCircle size={28} className="text-white mx-auto mb-4" />
            <h3 className="font-heading font-bold text-white mb-2">Become a partner</h3>
            <p className="text-sm text-white/80 leading-relaxed mb-4">Join the Partner Programme and start earning recurring commission on every referral.</p>
            <Link to="/partner-programme" className="inline-block bg-white text-brand-aqua-dark font-semibold rounded-full px-6 py-2.5 text-sm hover:bg-white/90 transition-colors" data-testid="partner-cta-partner">Become a Partner</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FAQSection() {
  const faqs = [
    { q: "What is a payment gateway?", a: "Think of it as a digital cashier. When a customer hits 'pay', the gateway takes their card details, encrypts them, and sends them off to the bank for approval. If everything checks out, the money moves and the customer gets their confirmation. All of that happens in a couple of seconds — and none of the sensitive data ever touches your client's server." },
    { q: "How does CanDo Payments compare to Stripe?", a: "Stripe is a brilliant product — but it's built for self-service, not support. We're priced lower than Stripe for most UK merchants, we offer real UK-based support, and we pay partners recurring commission for every client they bring across. Stripe doesn't do any of that." },
    { q: "Who's actually processing the payments?", a: "CanDo Payments is powered by Elavon — the merchant services arm of U.S. Bank. When your client pays with us, the transaction runs on the same infrastructure that handles payments for Virgin Atlantic, IKEA, Hilton and British Airways." },
    { q: "Is the platform PCI compliant?", a: "Yes. Every CanDo Payments integration inherits Elavon's PCI DSS compliance end-to-end. If you use our hosted payment pages, your client has almost nothing to do on their end to stay compliant." },
    { q: "How long does it take to settle funds?", a: "Next working day into your client's UK bank account as standard. We don't charge extra for next-day settlement — it's just how we work." },
    { q: "What does the partner commission look like?", a: "You earn recurring commission on every client you bring across — meaning you get paid for as long as your client is processing with us. The rate is tiered, so the more clients you onboard, the higher your share becomes." },
  ];
  return (
    <section className="py-20 bg-white" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3 text-center">FAQs</p>
        <h2 className="font-heading text-3xl font-bold text-brand-black tracking-tight text-center mb-10">Frequently asked questions</h2>
        <ResponsiveAccordion items={faqs} testIdPrefix="faq" />
      </div>
    </section>
  );
}

